import React, { useEffect, useRef, useState } from 'react';

import { Stack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { includes, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { PageContent } from 'components/Layout/PageContent';
import { AiBrainButton } from 'containers/App/AiBrainButton';
import { AppNav } from 'containers/App/AppNav';
import { AppRoutes } from 'containers/App/AppRoutes';
import { CloudInsightDrawer } from 'containers/App/CloudInsight';
import { GettingStarted } from 'containers/App/GettingStarted/index';
import { useUserPermission } from 'containers/App/hooks/permissions/useUserPermission';
import { KeycloakContext } from 'containers/App/keycloak/KeycloakContext';
import { DocsPanelLayout } from 'containers/Documentation/DocsPanel/DocsPanelLayout';
import { DocsPanelProvider } from 'containers/Documentation/DocsPanel/DocsPanelProvider';
import { alertsSaga } from 'containers/Findings/Alerts/saga';
import * as alerts from 'containers/Findings/Alerts/slice';
import { queryFilterSaga } from 'containers/QueryFilter/saga';
import * as queryFilter from 'containers/QueryFilter/slice';
import { cloudAccountsSaga } from 'containers/Setup/CloudAccounts/saga';
import * as accounts from 'containers/Setup/CloudAccounts/slice';
import { SharedStateContextProvider } from 'containers/SharedState/context/context';
import { sharedStateSaga } from 'containers/SharedState/saga';
import * as sharedState from 'containers/SharedState/slice';
import { useInjector } from 'utils/inject';

import { isTestEnv } from '../../../utils/env';
import { UserPermissionsProvider } from '../../components';
import { Organization } from '../HomePage/Organization';

import { Authenticating } from './Authenticating';
import { appSaga } from './saga';
import { selectApp } from './selectors';
import { actions, reducer, sliceKey } from './slice';

//import './printStyles.css';

export default function Main() {
  useInjector(sliceKey, reducer, appSaga);
  useInjector(queryFilter.sliceKey, queryFilter.reducer, queryFilterSaga);
  useInjector(alerts.sliceKey, alerts.reducer, alertsSaga);
  useInjector(accounts.sliceKey, accounts.reducer, cloudAccountsSaga);
  useInjector(sharedState.sliceKey, sharedState.reducer, sharedStateSaga);

  const { userPermission } = useUserPermission();
  const {
    user,
    organization,
    isCloudInsightDrawerOpen,
    cloudAccounts,
    environments,
    showGettingStarted,
  } = useSelector(selectApp);
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const { hostname, pathname } = useLocation();

  /*if no cloud account is onboarded, show welcome screen*/
  useEffect(() => {
    const show = localStorage.getItem('showWelcomeScreen');
    dispatch(
      actions.updateShowGettingStarted(
        //!show &&
        !isTestEnv() &&
          isEmpty(cloudAccounts.data) &&
          //!cloudAccounts.loading &&
          organization !== 'master',
      ),
    );
  }, [cloudAccounts.data, organization, dispatch]);

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      const A4_WIDTH = 1123;
      const A4_HEIGHT = 794;

      const contentWidth = content.scrollWidth;
      const contentHeight = content.scrollHeight;

      const scaleW = A4_WIDTH / contentWidth;
      const scaleH = A4_HEIGHT / contentHeight;
      const finalScale = Math.min(scaleW, scaleH, 1);

      setScale(finalScale);
    }
  }, []);

  //set if it is dev or test org
  useEffect(() => {
    //console.log('hostname', hostname);
    dispatch(
      sharedState.actions.updateIsDevORTestOrg(
        includes(['localhost', 'dev.ariksa.io', 'staging.ariksa.io'], hostname),
      ),
    );
  }, [hostname, dispatch]);

  const onOpenCloudInsight = () =>
    dispatch(actions.updateIsCloudInsightDrawerOpen(true));
  const onCloseCloudInsight = () =>
    dispatch(actions.updateIsCloudInsightDrawerOpen(false));

  return (
    <>
      <KeycloakContext>
        {!user.info.id && (
          <>{!!organization ? <Authenticating /> : <Organization />}</>
        )}
        {user.info.id && (
          <UserPermissionsProvider value={{ userPermission }}>
            <SharedStateContextProvider value={{ contentRef }}>
              {/* The docs follow-along panel docks beside the whole app. */}
              <DocsPanelProvider>
                <DocsPanelLayout>
                  <Stack
                    h={'full'}
                    w={'full'}
                    pos={'absolute'}
                    spacing={0}
                    overflowY="auto"
                    minW="minUIWidth"
                    ref={contentRef}
                  >
                    <AppNav
                      isOpenWelcomeScreen={showGettingStarted}
                      onOpenCloudInsight={onOpenCloudInsight}
                    />
                    <PageContent>
                      {/* show sentry errors in console */}
                      <Sentry.ErrorBoundary
                        onError={error => {
                          console.dir(error);
                          // alert('Backend is updated. UI needs reload');
                          // window.location.reload();
                        }}
                      >
                        {cloudAccounts.loading || environments.isLoading ? (
                          <CustomSpinner size="xl" />
                        ) : (
                          <>
                            {!showGettingStarted && <AiBrainButton />}
                            {/*Open welcome screen on first login*/}
                            {showGettingStarted &&
                            !pathname?.startsWith('/setup/accounts/add/') ? (
                              <GettingStarted />
                            ) : (
                              <>
                                {isCloudInsightDrawerOpen && (
                                  <CloudInsightDrawer
                                    isOpen={isCloudInsightDrawerOpen}
                                    onClose={onCloseCloudInsight}
                                  />
                                )}
                                <AppRoutes />
                              </>
                            )}
                          </>
                        )}
                      </Sentry.ErrorBoundary>
                    </PageContent>
                  </Stack>
                </DocsPanelLayout>
              </DocsPanelProvider>
            </SharedStateContextProvider>
          </UserPermissionsProvider>
        )}
      </KeycloakContext>
    </>
  );
}
